import React, { useState, useCallback } from 'react';

interface ToastProps {
  id: string;
  title: string;
  description?: string;
}

interface ToastState {
  toasts: ToastProps[];
}

let toasts: ToastProps[] = [];
let listeners: Array<(state: ToastState) => void> = [];

function ensureLiveRegion(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  let node = document.getElementById('toast-live-region');
  if (!node) {
    node = document.createElement('div');
    node.id = 'toast-live-region';
    node.setAttribute('role', 'status');
    node.setAttribute('aria-live', 'polite');
    node.setAttribute('aria-atomic', 'true');
    Object.assign(node.style, {
      position: 'absolute',
      width: '1px',
      height: '1px',
      margin: '-1px',
      border: '0',
      padding: '0',
      overflow: 'hidden',
      clip: 'rect(0 0 0 0)',
      clipPath: 'inset(50%)',
      whiteSpace: 'pre',
    } as CSSStyleDeclaration);
    document.body.appendChild(node);
  }
  return node;
}

const toastState: { current: ToastState } = {
  current: { toasts },
};

function setState(newState: ToastState) {
  toastState.current = newState;
  listeners.forEach((listener) => listener(toastState.current));
  const live = ensureLiveRegion();
  if (live) {
    const last = newState.toasts[newState.toasts.length - 1];
    live.textContent = last ? [last.title, last.description].filter(Boolean).join('\n') : '';
  }
}

export function useSimpleToast() {
  const [state, setStateListener] = useState(toastState.current);

  const toast = useCallback(({ title, description }: { title: string; description?: string }) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: ToastProps = { id, title, description };
    toasts = [...toasts, newToast];
    setState({ toasts });

    setTimeout(() => {
      toasts = toasts.filter((t) => t.id !== id);
      setState({ toasts });
    }, 5000);
  }, []);

  React.useEffect(() => {
    listeners.push(setStateListener);
    return () => {
      listeners = listeners.filter((listener) => listener !== setStateListener);
    };
  }, []);

  return { ...state, toast };
}
